import React, { forwardRef, useMemo, useRef, useImperativeHandle } from "react";
import { Grid, Box, Typography } from "@mui/material";
import ConsolidatedStackedAreaChat, {
  ConsolidatedStackedAreaChatRef,
} from "./ConsolidatedStackedAreaChat";

type RawData = {
  year: string;
  [key: string]: number | string;
};

type TimelineAndFinancialMetricsProps = {
  data: RawData[];
  width: number;
  height: number;
};

export type TimelineAndFinancialMetricsRef = {
  downloadChart: (format: "png" | "jpeg") => void;
  _testChartRef?: React.RefObject<any>;
};

const toSafeNumber = (value: unknown, fallback: number): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toSafeString = (value: unknown, fallback = ""): string => {
  return typeof value === "string" ? value : fallback;
};

const sanitizeTimelineData = (input: RawData[] | undefined): RawData[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .filter((item) => item != null && typeof item === "object")
    .map((item) => {
      const safeItem: RawData = {
        year: toSafeString(item?.year, ""),
      };

      Object.keys(item).forEach((key) => {
        if (key === "year") {
          safeItem.year = toSafeString(item[key], "");
          return;
        }

        const value = item[key];

        if (typeof value === "number") {
          safeItem[key] = Number.isFinite(value) ? value : 0;
        } else if (typeof value === "string") {
          const parsed = Number(value);
          safeItem[key] = Number.isFinite(parsed) ? parsed : 0;
        } else {
          safeItem[key] = 0;
        }
      });

      return safeItem;
    })
    .filter((item) => item.year !== "");
};

const TimelineAndFinancialMetrics = forwardRef<
  TimelineAndFinancialMetricsRef,
  TimelineAndFinancialMetricsProps
>(({ data, width, height }, ref) => {
  const chartRef = useRef<ConsolidatedStackedAreaChatRef>(null);

  const safeData = useMemo(() => sanitizeTimelineData(data), [data]);
  const safeWidth = toSafeNumber(width, 1100);
  const safeHeight = toSafeNumber(height, 450);

  useImperativeHandle(ref, () => ({
    downloadChart: (format: "png" | "jpeg") => {
      if (!chartRef.current) {
        console.warn("TimelineAndFinancialMetrics chart reference is not available.");
        return;
      }

      if (!safeData.length) {
        console.warn("No valid timeline data available for download.");
        return;
      }

      chartRef.current.downloadChart(format);
    },
    _testChartRef: chartRef,
  }));

  if (!safeData.length) {
    return (
      <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
        <Box
          sx={{
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <Typography sx={{ color: "#64748b", fontSize: "0.95rem" }}>
            No valid data available for Decarbonization Pathway.
          </Typography>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
      <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
        <ConsolidatedStackedAreaChat
          ref={chartRef}
          data={safeData}
          width={safeWidth}
          height={safeHeight}
          chartType="TimelineAndFinancialMetrics"
        />
      </Grid>
    </Grid>
  );
});

export default TimelineAndFinancialMetrics;